import type { SchemaFieldDefinition } from "@erp/shared-types";
import type { SocketType } from "../types";
import { extractionSchemaToOutputSockets } from "./extractionSchemaToSockets";

export interface ObserveDataSourceLinkOutputSocketSpec {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/**
 * Output sockets the Observe DataSource Link trigger always emits, reserved so
 * they can't collide with a schema field id.
 */
export const OBSERVE_DATA_SOURCE_LINK_RESERVED_OUTPUT_SOCKET_IDS = [
  "dataSourceLink",
  "dataSourceTitle",
  "phase",
  "metadataId",
  "changeOperation",
  "changeLsn",
] as const;

// Schema field names map directly to ingest column names, which may be upper-
// or lower-case (e.g. AS/400 `ITEMMAST5` columns are upper-case), so — unlike
// the http_trigger helper — both cases are allowed as socket ids.
const SOCKET_ID_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/;

/**
 * Output sockets for `observe_data_source_link`, item-shaped:
 *   - `dataSourceLink` (json) — the observed link `{ id, dataSourceId,
 *     schemaName, tableName }`. Single value (broadcast across the batch).
 *   - `dataSourceTitle` (string) — the observed link's DataSource title. Single
 *     value (broadcast). The only scalar in the run that says WHICH source
 *     changed, which is what a per-source lookup has to match on when the
 *     source's own rows carry no such column.
 *   - `metadataId` (array<uuid>) — the surrogate `id` of each row in the batch
 *     (on the initial phase, every row of the table).
 *   - `changeOperation` (array<string>) — the CDC op per row.
 *   - one socket per eligible root schema field, id = `fieldName` (= the ingest
 *     column name), each carrying that column's values across the batch as an
 *     array, index-aligned with `metadataId`.
 *
 * A downstream `batch()` expression zips these per-field arrays (broadcasting
 * `dataSourceLink`) back into per-item objects.
 */
export function observeDataSourceLinkRowFieldsToOutputSockets(
  rootFields: SchemaFieldDefinition[],
): ObserveDataSourceLinkOutputSocketSpec[] {
  const reservedIds = new Set<string>(
    OBSERVE_DATA_SOURCE_LINK_RESERVED_OUTPUT_SOCKET_IDS,
  );

  const eligible = rootFields.filter((field) => {
    if (field.parentFieldId) return false;
    if (!SOCKET_ID_REGEX.test(field.fieldName)) return false;
    if (reservedIds.has(field.fieldName)) return false;
    return true;
  });

  // wrapInArray: every per-field socket carries the batch column as an array.
  const perField = extractionSchemaToOutputSockets(eligible, {
    idPrefix: "",
    mode: "per_field",
    wrapInArray: true,
  });

  return [
    {
      id: "dataSourceLink",
      name: "Data Source Link",
      type: "json" as SocketType,
      required: false,
      description:
        "The observed DataSource Link ({ id, dataSourceId, schemaName, tableName }).",
    },
    {
      id: "dataSourceTitle",
      name: "Data Source Title",
      type: "string" as SocketType,
      required: false,
      description:
        "Title of the observed link's DataSource. Broadcast across the batch. A per-source file family (one table per source) carries no column saying which source it is, so this is the only value a lookup can key on to translate that source's private codes.",
    },
    {
      id: "phase",
      name: "Phase",
      type: "string" as SocketType,
      required: false,
      description:
        "'initial' (full backfill, read from the table) or 'change' (a CDC batch).",
    },
    {
      id: "metadataId",
      name: "Metadata Id",
      type: "array<uuid>" as SocketType,
      required: false,
      description:
        "Surrogate `id` of each row in the batch (initial: every table row), index-aligned with the field columns.",
    },
    {
      id: "changeOperation",
      name: "Change Operation",
      type: "array<string>" as SocketType,
      required: false,
      description:
        "'insert' | 'update' | 'delete' per row, index-aligned with the field columns. Always 'insert' on the initial phase. A delete carries its BEFORE image, so a sink that only upserts would write the row back — wire this to decide.",
    },
    {
      id: "changeLsn",
      name: "Change LSN",
      type: "array<string>" as SocketType,
      required: false,
      description:
        "Source sequence number per row, or the initial-ingest watermark. Use for idempotent ordered writes.",
    },
    ...perField.map((socket) => ({
      id: socket.id,
      name: socket.name,
      type: socket.type,
      required: socket.required,
      description: socket.description,
    })),
  ];
}
